import { Injectable, inject } from '@angular/core';

import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { sumBy } from 'lodash-es';
import { NEVER, Subject, combineLatest, distinctUntilKeyChanged, switchMap } from 'rxjs';

import { BreadcrumbsSlotService } from '../breadcrumbs/breadcrumbs-slot.service';

interface LiveNowLinkData {
    sports: CountItem[];
    url?: string;
}

const loadLiveNowItemComponent = () => import('./live-now-item.component').then((c) => c.LiveNowItemComponent);

@Injectable({ providedIn: 'root' })
export class LiveNowLinkRegisterService {
    private adaptiveLayout = inject(AdaptiveLayoutService);
    private breadcrumbSlot = inject(BreadcrumbsSlotService);

    private data$ = new Subject<LiveNowLinkData | void>();

    constructor() {
        const productUtils = inject(SportsProductUtils);

        const layoutState$ = this.adaptiveLayout.stateChange$.pipe(distinctUntilKeyChanged('headerSubNavigation'));

        combineLatest([this.data$, layoutState$])
            .pipe(
                productUtils.filterSportsEmitLast(),
                switchMap(([data, state]) => {
                    if (!data?.sports?.length || !state.headerSubNavigation) {
                        this.breadcrumbSlot.clearSlot();

                        return NEVER;
                    }

                    const liveCount = sumBy(data.sports, (sport: CountItem) => sport.counts.live);

                    if (liveCount) {
                        return loadLiveNowItemComponent().then((component) => ({
                            component,
                            inputs: {
                                count: liveCount,
                                liveUrl: data.url,
                                sport: data.sports.length === 1 && data.sports[0].id ? data.sports[0] : undefined,
                            },
                        }));
                    }
                    this.breadcrumbSlot.clearSlot();

                    return NEVER;
                }),
            )
            .subscribe(({ component, inputs }) => this.setLiveButton(component, inputs));
    }

    addLiveButtonWithMultipleSports(sports: CountItem[], url: string): void {
        this.data$.next({ sports, url });
    }

    addLiveButton(sport?: CountItem, url?: string): void {
        this.data$.next({ sports: sport ? [sport] : [], url });
    }

    removeLiveButton(): void {
        this.data$.next();
    }

    private setLiveButton(
        component: any,
        inputs: {
            count: number;
            liveUrl?: string;
            sport?: CountItem;
        },
    ): void {
        this.breadcrumbSlot.setSlot({
            component,
            inputs,
        });
    }
}
